import React, { useEffect, useRef } from "react";
import { useDrop } from "react-dnd";
import { Paper, Typography, Box } from "@mui/material";
import CandidateCard from "../CandidateCard/CandidateCard";
import { Candidate, DragItem, ItemTypes } from "../types/CandidateBoardTypes";
import { List, CellMeasurer, CellMeasurerCache } from "react-virtualized";

interface CandidateColumnProps {
  status: string;
  candidates: Candidate[];
  onDrop: (id: string, newStatus: string, currentStatus: string) => void;
  onCandidateDoubleClick: (candidate: Candidate) => void;
}

const CandidateColumn: React.FC<CandidateColumnProps> = ({
  status,
  candidates,
  onDrop,
  onCandidateDoubleClick,
}) => {
  // Virtualization cache to measure dynamic card heights
  const cacheRef = useRef(
    new CellMeasurerCache({ fixedWidth: true, defaultHeight: 120 })
  );

  useEffect(() => {
    // Clear measurements when candidate set changes
    cacheRef.current.clearAll();
  }, [candidates]);

  const [, drop] = useDrop<DragItem>(() => ({
    accept: ItemTypes.CANDIDATE,
    drop: (item: DragItem) => {
      if (item.currentStatus !== status) {
        onDrop(item.id, status, item.currentStatus);
      }
    },
  }),
    [status, onDrop]
  );

  // Fixed visual list dimensions (matches FleetCRM pattern). Adjust if needed.
  const LIST_HEIGHT = 760; // px - column height to enable scrolling inside
  const LIST_WIDTH = 270;

  const RenderRow = ({ index, key, style, parent }: any) => {
    const candidate = candidates[index];
    return (
      <CellMeasurer
        key={key}
        cache={cacheRef.current}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ registerChild }) => (
          <div ref={registerChild} style={style}>
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              onDoubleClick={onCandidateDoubleClick}
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  return (
    <Paper
      ref={drop}
      sx={{
        flex: 1,
        height: "100%",
        margin: 1,
        backgroundColor: "#f5f5f5",
      }}
      variant="outlined"
    >
      <Typography variant="h6" align="center" gutterBottom>
        {status}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
        }}
      >
        <List
          height={LIST_HEIGHT}
          rowCount={candidates.length}
          deferredMeasurementCache={cacheRef.current}
          rowHeight={cacheRef.current.rowHeight}
          width={LIST_WIDTH}
          rowRenderer={RenderRow}
          overscanRowCount={5}
          data-testid={`candidate-column-${status}`}
        />
      </Box>
    </Paper>
  );
};

export default CandidateColumn;
